import { Box, Flex, HStack, IconButton, Text, VStack } from '@chakra-ui/react'
import { UniqueIdentifier } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { ObjectDataTypeConfig } from '@helo/cms'
import { isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useOnChange } from '../../hooks/useOnChange'
import { matchSubstring } from '../../utils/textMatch'
import {
  ColumnState,
  TableColumnConfig,
  TableColumnConfigUtils,
} from '../../widgets/TableWidget/tablewidget.parts'
import { HighlightText } from '../HighlightText/HighlightText'
import { Icon } from '../Icons/Icon'
import { SearchIcon } from '../Icons/SearchIcon'
import { Tooltip } from '../Tooltip/Tooltip'
import { SortableItem, SortableList } from '../drag-and-drop'
import { CustomDndContext as DndContext } from '../drag-and-drop/DndContext'
import { BaseInput } from '../inputs/BaseInput/BaseInput'
import { HiddenIconButton, VisibleIconButton } from './TableStateEditor.parts'

type TableStateEditorProps = {
  onChange?: (value: ColumnState[]) => void
  value: ColumnState[]

  // Backward compatible: existing approach
  columnConfigs?: TableColumnConfig[]

  // New approach: pass ObjectDataTypeConfig (used as fallback if columnConfigs don't have enough info)
  objectDataTypeConfig?: ObjectDataTypeConfig
}

type TableStateEditorColumn = ColumnState & {
  label: string
  id: UniqueIdentifier
}

const partitionEditorColumns = (
  columnStates: ColumnState[],
  configs: TableColumnConfig[],
): Record<UniqueIdentifier, TableStateEditorColumn[]> => {
  const items = columnStates.map<TableStateEditorColumn>(columnState => ({
    ...columnState,
    label:
      (configs.find(({ key }) => key === columnState.key)?.config?.state
        ?.label as string) ?? columnState.key,
    id: columnState.key,
  }))

  return items.reduce<Record<UniqueIdentifier, TableStateEditorColumn[]>>(
    (acc, item) => {
      if (item.visible) {
        acc.visible.push(item)
      } else {
        acc.hidden.push(item)
      }
      return acc
    },
    { visible: [], hidden: [] },
  )
}

export const TableStateEditor = ({
  value,
  onChange = () => {},
  columnConfigs: propsColumnConfigs,
  objectDataTypeConfig,
}: TableStateEditorProps) => {
  // Determine which columnConfigs to use
  const columnConfigs = useMemo(() => {
    // Backward compatibility: use provided columnConfigs if available
    if (propsColumnConfigs) {
      return propsColumnConfigs
    }

    // Fallback: extract from ObjectDataTypeConfig if columnConfigs not provided
    if (objectDataTypeConfig) {
      const autoDiscovered =
        TableColumnConfigUtils.extractColumnsFromObjectConfig(
          objectDataTypeConfig,
        )
      return autoDiscovered
    }

    // Fallback
    return []
  }, [propsColumnConfigs, objectDataTypeConfig])

  /* For dnd, there needs to be two arrays, one for visible and one for hidden. sortedItems acts as a proxy. */
  const [sortedItems, setSortedItems] = useState<
    Record<UniqueIdentifier, TableStateEditorColumn[]>
  >(() => partitionEditorColumns(value, columnConfigs))

  /** Keeps DnD lists in sync when `value` is reset from outside (e.g. drawer close discards edits). */
  const valueSyncSignature = useMemo(
    () =>
      JSON.stringify(
        value.map(c => ({
          k: c.key,
          v: c.visible,
          p: c.pinned,
        })),
      ),
    [value],
  )

  useEffect(() => {
    setSortedItems(prev => {
      const next = partitionEditorColumns(value, columnConfigs)
      return isEqual(prev, next) ? prev : next
    })
  }, [valueSyncSignature, columnConfigs, value])

  const findContainer = (id: UniqueIdentifier) => {
    if (id in sortedItems) {
      return id
    }
  }

  useOnChange(() => {
    const newItems = sortedItems.visible
      .concat(sortedItems.hidden)
      .map(item => {
        const { id, label, ...rest } = item
        return rest
      })

    onChange?.(newItems)
  }, [sortedItems])

  const [searchText, setSearchText] = useState('')
  const searchActive = searchText.trim() !== ''

  const matchesSearch = useCallback(
    (item: TableStateEditorColumn) => matchSubstring(item.label, searchText),
    [searchText],
  )

  /** Rows shown for the current search. While editing, header bulk actions act on these only. */
  const displayedVisible = useMemo(
    () => sortedItems.visible.filter(matchesSearch),
    [sortedItems.visible, matchesSearch],
  )
  const displayedHidden = useMemo(
    () => sortedItems.hidden.filter(matchesSearch),
    [sortedItems.hidden, matchesSearch],
  )

  /** True when every shown visible column is pinned. */
  const allShownVisiblePinned =
    displayedVisible.length > 0 && displayedVisible.every(c => c.pinned)

  const togglePinAllVisible = () => {
    const nextPinned = !allShownVisiblePinned
    setSortedItems(items => ({
      ...items,
      visible: items.visible.map(c =>
        matchesSearch(c) ? { ...c, pinned: nextPinned } : c,
      ),
    }))
  }

  return (
    <VStack align="stretch" gap="16px">
      <BaseInput
        data-testid="table-state-editor-search-input"
        placeholder="Search"
        value={searchText}
        onChange={event => setSearchText(event.target.value)}
        rightInputElement={
          searchText ? (
            <IconButton
              aria-label="Clear search"
              data-testid="table-state-editor-search-clear"
              variant="unstyled"
              minW="auto"
              boxSize="16px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onClick={() => setSearchText('')}
              icon={
                <Icon
                  icon="XIcon"
                  boxSize="14px"
                  color="presets.turquoise.100"
                />
              }
            />
          ) : (
            <SearchIcon boxSize="16px" color="presets.turquoise.100" />
          )
        }
      />
      <DndContext
        onDragOver={({ active, over }) => {
          if (searchActive) return
          const overContainer = findContainer(
            over?.data.current?.sortable.containerId,
          )
          const activeContainer = findContainer(
            active?.data.current?.sortable.containerId,
          )

          if (
            typeof overContainer === 'undefined' ||
            typeof activeContainer === 'undefined'
          ) {
            return
          }

          if (overContainer !== activeContainer) {
            setSortedItems(items => {
              const activeItems = items[activeContainer]
              const overItems = items[overContainer]

              const overItem = overItems.find(({ id }) => id === over?.id)
              const overIndex = overItems.indexOf(overItem!)
              const activeItem = activeItems.find(({ id }) => id === active?.id)
              const activeIndex = activeItems.indexOf(activeItem!)

              let newIndex: number

              if (overItems.find(({ id }) => id === over?.id)) {
                newIndex = overItems.length + 1
              } else {
                const isBelowOverItem =
                  over &&
                  active.rect.current.translated &&
                  active.rect.current.translated.top >
                    over.rect.top + over.rect.height

                const modifier = isBelowOverItem ? 1 : 0

                newIndex =
                  overIndex >= 0 ? overIndex + modifier : overItems.length + 1
              }

              return {
                ...items,
                [activeContainer]: items[activeContainer].filter(
                  item => item !== activeItem,
                ),
                [overContainer]: [
                  ...items[overContainer].slice(0, newIndex),
                  {
                    ...items[activeContainer][activeIndex],
                    visible: overContainer === 'visible',
                  },
                  ...items[overContainer].slice(
                    newIndex,
                    items[overContainer].length,
                  ),
                ],
              }
            })
          }
        }}
        onDragEnd={({ active, over }) => {
          if (searchActive) return
          const overContainer = findContainer(
            over?.data.current?.sortable.containerId,
          )
          const activeContainer = findContainer(
            active?.data.current?.sortable.containerId,
          )

          if (
            typeof overContainer === 'undefined' ||
            typeof activeContainer === 'undefined'
          ) {
            return
          }

          if (over && active.id !== over?.id) {
            const overContainer = findContainer(
              over?.data.current?.sortable.containerId,
            )

            if (overContainer!) {
              const activeIndex = sortedItems[activeContainer].findIndex(
                ({ id }) => id === active.id,
              )
              const overIndex = sortedItems[overContainer].findIndex(
                ({ id }) => id === over.id,
              )

              if (activeIndex !== overIndex) {
                setSortedItems(items => {
                  return {
                    ...items,
                    visible: items.visible,

                    // Reorder follows the drop position exactly; the editor
                    // does not resort pinned columns to the front. Pinned-first
                    // ordering is applied on Apply (save). Moving a column into
                    // the hidden list clears its pin.
                    [overContainer]: arrayMove(
                      items[overContainer].map(item =>
                        overContainer === 'hidden'
                          ? { ...item, pinned: false }
                          : item,
                      ),
                      activeIndex,
                      overIndex,
                    ) as TableStateEditorColumn[],
                  }
                })
              }
            }
          }
        }}
      >
        <VStack align="stretch" gap="18px">
          {[
            {
              containerLabel: 'Visible Columns',
              containerId: 'visible' as const,
              items: displayedVisible,
              headerActions: (
                <HStack gap="3px" alignItems="center">
                  <Tooltip
                    label={
                      allShownVisiblePinned
                        ? 'Unpin all columns'
                        : 'Pin all columns'
                    }
                    openDelay={500}
                    placement="top"
                  >
                    <IconButton
                      aria-label={
                        allShownVisiblePinned
                          ? 'Unpin all columns'
                          : 'Pin all columns'
                      }
                      data-testid="table-state-editor-pin-all-button"
                      aria-pressed={allShownVisiblePinned}
                      color="presets.neutral.30"
                      p="15px"
                      fontSize="22px"
                      boxSize="20px"
                      icon={
                        <Icon
                          icon="PinIcon"
                          transform={
                            allShownVisiblePinned ? 'none' : 'rotate(45deg)'
                          }
                          boxSize="22px"
                        />
                      }
                      onClick={togglePinAllVisible}
                    />
                  </Tooltip>
                  <Tooltip label="Hide All" openDelay={500} placement="top">
                    <HiddenIconButton
                      data-testid="table-state-editor-hide-all-button"
                      color="presets.turquoise.60"
                      onClick={() => {
                        setSortedItems(items => ({
                          visible: items.visible.filter(
                            item => !matchesSearch(item),
                          ),
                          hidden: [
                            ...items.hidden,
                            ...items.visible
                              .filter(matchesSearch)
                              .map(item => ({
                                ...item,
                                visible: false,
                                pinned: false,
                              })),
                          ],
                        }))
                      }}
                    />
                  </Tooltip>
                </HStack>
              ),
            },
            {
              containerLabel: 'Hidden Columns',
              containerId: 'hidden' as const,
              items: displayedHidden,
              headerActions: (
                <Tooltip label="Show All" openDelay={500} placement="top">
                  <VisibleIconButton
                    data-testid="table-state-editor-show-all-button"
                    onClick={() => {
                      setSortedItems(items => ({
                        hidden: items.hidden.filter(
                          item => !matchesSearch(item),
                        ),
                        visible: [
                          ...items.visible,
                          ...items.hidden.filter(matchesSearch).map(item => ({
                            ...item,
                            visible: true,
                          })),
                        ],
                      }))
                    }}
                  />
                </Tooltip>
              ),
            },
          ].map(({ containerLabel, containerId, items, headerActions }) => {
            return (
              <Box key={containerId}>
                <Flex
                  alignItems="center"
                  gap="6px"
                  pl={containerId === 'visible' ? '24px' : '16px'}
                  pr="8px"
                  py="8px"
                >
                  <Text
                    flex="1"
                    minW={0}
                    color="presets.neutral.60"
                    fontSize="12px"
                    fontWeight="600"
                    lineHeight="15px"
                    textTransform="uppercase"
                  >
                    {containerLabel}
                  </Text>
                  {headerActions}
                </Flex>
                <SortableList
                  key={containerId}
                  id={containerId}
                  data-testid={`table-state-editor-${containerId}-list`}
                  items={items}
                  minHeight="32px"
                  renderItem={(item: TableStateEditorColumn) => (
                    <SortableItem
                      id={item.id}
                      disabled={searchActive}
                      data-testid={`table-state-editor-column-${item.key}`}
                      borderTop="1px"
                      borderBottom="1px"
                      borderColor="presets.neutral.20"
                      backgroundColor="presets.neutral.0"
                      marginTop="-1px"
                      _hover={{
                        backgroundColor: 'presets.neutral.10',
                      }}
                    >
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        flex="1"
                        minW={0}
                      >
                        <HighlightText
                          text={item.label}
                          searchTerm={searchText}
                          matchMode="substring"
                          fontSize="12px"
                          fontWeight="600"
                          lineHeight="15px"
                          color={
                            containerId === 'visible'
                              ? 'presets.turquoise.100'
                              : 'presets.neutral.50'
                          }
                        />
                        <HStack gap="3px" alignItems="center">
                          <Tooltip
                            openDelay={500}
                            label={item.pinned ? 'Unpin Column' : 'Pin Column'}
                          >
                            <IconButton
                              aria-label={item.pinned ? 'Unpin' : 'Pin'}
                              data-testid={`table-state-editor-column-pin-${item.key}`}
                              color={
                                item.pinned
                                  ? 'presets.turquoise.60'
                                  : 'presets.neutral.30'
                              }
                              p="15px"
                              fontSize="22px"
                              boxSize="20px"
                              icon={
                                <Icon
                                  icon="PinIcon"
                                  transform={
                                    item.pinned ? 'none' : 'rotate(45deg)'
                                  }
                                  boxSize="22px"
                                />
                              }
                              onClick={() => {
                                const columnKey = item.key
                                setSortedItems(items => {
                                  // Pinning a visible column toggles the flag in
                                  // place — the editor does not resort. Pinned-
                                  // first ordering is applied on Apply (save).
                                  if (
                                    items.visible.some(c => c.key === columnKey)
                                  ) {
                                    return {
                                      hidden: items.hidden,
                                      visible: items.visible.map(c =>
                                        c.key === columnKey
                                          ? { ...c, pinned: !c.pinned }
                                          : c,
                                      ),
                                    }
                                  }

                                  // Pinning a hidden column implies showing it;
                                  // it moves to the end of the visible list
                                  // (no resort) and becomes pinned + visible.
                                  const self =
                                    items.hidden.find(
                                      c => c.key === columnKey,
                                    ) ?? item
                                  return {
                                    hidden: items.hidden.filter(
                                      column => column.key !== columnKey,
                                    ),
                                    visible: [
                                      ...items.visible,
                                      { ...self, pinned: true, visible: true },
                                    ],
                                  }
                                })
                              }}
                            />
                          </Tooltip>
                          {item.visible ? (
                            <Tooltip openDelay={500} label="Column Is Visible">
                              <VisibleIconButton
                                data-testid={`table-state-editor-column-visibility-${item.key}`}
                                onClick={() =>
                                  setSortedItems(items => ({
                                    visible: items.visible.filter(
                                      column => column.key !== item.key,
                                    ),
                                    hidden: [
                                      ...items.hidden,
                                      {
                                        ...item,
                                        visible: false,
                                        pinned: false,
                                      },
                                    ],
                                  }))
                                }
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip
                              openDelay={500}
                              label="Column Is Not Visible"
                            >
                              <HiddenIconButton
                                data-testid={`table-state-editor-column-visibility-${item.key}`}
                                onClick={() =>
                                  setSortedItems(items => ({
                                    visible: [
                                      ...items.visible,
                                      { ...item, visible: true },
                                    ],
                                    hidden: items.hidden.filter(
                                      column => column.key !== item.key,
                                    ),
                                  }))
                                }
                              />
                            </Tooltip>
                          )}
                        </HStack>
                      </Flex>
                    </SortableItem>
                  )}
                />
              </Box>
            )
          })}
        </VStack>
      </DndContext>
    </VStack>
  )
}
